import React, { useEffect, useState } from 'react'
import { Alert, Button, Form, Nav, Row, Col, Spinner } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStockRange, fetchStockPrices, setSymbol, setDateRange, clearSymbol } from '../../../reducers/stockSlice'
import { fetchSnp500 } from '../../../reducers/snp500Slice'
import StockRangeInfo from '../../../components/StockRangeInfo'
import StockSymbolInput from '../../../components/StockSymbolInput'
import StockPriceChart from '../../../components/StockPriceChart'
import StockPriceTable from '../../../components/StockPriceTable'

function AdminStock() {
  const dispatch = useDispatch()
  const { symbol, start, end, range, rangeLoading, rangeError, prices, loading, error } =
    useSelector((state) => state.stock)
  const snp500Items = useSelector((state) => state.snp500.items)

  const [localStart, setLocalStart] = useState(start || '')
  const [localEnd, setLocalEnd] = useState(end || '')

  useEffect(() => {
    dispatch(fetchStockRange())
    if (snp500Items.length === 0) dispatch(fetchSnp500())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (start) setLocalStart(start)
    if (end) setLocalEnd(end)
  }, [start, end])

  function handleSymbolSelect(sym) {
    dispatch(setSymbol(sym))
    dispatch(fetchStockPrices({ symbol: sym, start: null, end: null }))
  }

  function handleSymbolClear() {
    dispatch(clearSymbol())
    setLocalStart('')
    setLocalEnd('')
  }

  function handleApply() {
    const s = localStart || null
    const e = localEnd || null
    dispatch(setDateRange({ start: s, end: e }))
    dispatch(fetchStockPrices({ symbol, start: s, end: e }))
  }

  const symbols = snp500Items.map((i) => i.Symbol)
  const minDate = range?.oldest_date
  const maxDate = range?.newest_date

  return (
    <section className="py-4">
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/charities">Charity Manager</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/sre">SRE Events</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/stock">Stock Pricing</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/snp500">S&amp;P 500 List</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/investments">Investments</Nav.Link>
        </Nav.Item>
      </Nav>

      <StockRangeInfo range={range} loading={rangeLoading} error={rangeError} />

      <div className="mb-3 d-flex align-items-start gap-2">
        <div className="flex-grow-1">
          <StockSymbolInput
            symbols={symbols}
            selected={symbol}
            onSelect={handleSymbolSelect}
            onClear={handleSymbolClear}
          />
        </div>
        <Button variant="outline-secondary" onClick={handleSymbolClear} style={{ marginTop: '1.75rem' }}>
          Reset
        </Button>
      </div>

      {!symbol && (
        <p className="text-muted">Enter a symbol above to view pricing data.</p>
      )}

      {symbol && (
        <>
          <Row className="g-2 align-items-end mb-3">
            <Col xs={12} sm="auto">
              <Form.Label htmlFor="stock-start">Start</Form.Label>
              <Form.Control
                id="stock-start"
                type="date"
                value={localStart}
                min={minDate}
                max={maxDate}
                onChange={(e) => setLocalStart(e.target.value)}
              />
            </Col>
            <Col xs={12} sm="auto">
              <Form.Label htmlFor="stock-end">End</Form.Label>
              <Form.Control
                id="stock-end"
                type="date"
                value={localEnd}
                min={minDate}
                max={maxDate}
                onChange={(e) => setLocalEnd(e.target.value)}
              />
            </Col>
            <Col xs={12} sm="auto">
              <Button variant="primary" onClick={handleApply}>Apply</Button>
            </Col>
          </Row>

          {loading && (
            <div className="my-3">
              <Spinner animation="border" size="sm" role="status">
                <span className="visually-hidden">Loading…</span>
              </Spinner>
            </div>
          )}

          {error && !loading && (
            <Alert variant="danger" role="alert">
              {error}{' '}
              <Alert.Link
                as="button"
                onClick={() => dispatch(fetchStockPrices({ symbol, start, end }))}
              >
                Retry
              </Alert.Link>
            </Alert>
          )}

          {!loading && !error && prices.length === 0 && (
            <p className="text-muted">No data available for this date range.</p>
          )}

          {!loading && !error && prices.length > 0 && (
            <>
              <StockPriceChart prices={prices} symbol={symbol} />
              <StockPriceTable prices={prices} />
            </>
          )}
        </>
      )}
    </section>
  )
}

export default AdminStock
